import * as React from "react";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import { useI18n } from "../../lib/i18n";

function parseOtpDigits(value: string, maxLen = 6): string[] {
  const digits = value.replace(/\D/g, "").slice(0, maxLen).split("");
  while (digits.length < maxLen) digits.push("");
  return digits;
}

function isOtpComplete(digits: string[]): boolean {
  return digits.length === 6 && digits.every((d) => d !== "");
}

export function useOtpState(options?: {
  submitRef?: React.RefObject<HTMLButtonElement | null>;
}) {
  const submitRef = options?.submitRef;
  const [digits, setDigits] = React.useState(["", "", "", "", "", ""]);
  const refs = React.useRef(
    Array.from({ length: 6 }, () => React.createRef<HTMLInputElement>()),
  ).current as React.RefObject<HTMLInputElement | null>[];
  const pendingSubmitFocus = React.useRef(false);

  const reset = React.useCallback(() => setDigits(["", "", "", "", "", ""]), []);

  const blurInputs = React.useCallback(() => {
    refs.forEach((r) => r.current?.blur());
  }, [refs]);

  const queueSubmitFocus = React.useCallback(() => {
    pendingSubmitFocus.current = true;
    blurInputs();
  }, [blurInputs]);

  React.useLayoutEffect(() => {
    if (!pendingSubmitFocus.current || !isOtpComplete(digits) || !submitRef?.current) return;
    pendingSubmitFocus.current = false;
    submitRef.current.focus();
  }, [digits, submitRef]);

  const applyDigits = React.useCallback(
    (next: string[]) => {
      setDigits(next);
      if (isOtpComplete(next)) {
        queueSubmitFocus();
        return;
      }
      let lastIdx = -1;
      for (let j = 5; j >= 0; j--) {
        if (next[j]) {
          lastIdx = j;
          break;
        }
      }
      if (lastIdx >= 0) refs[lastIdx].current?.focus();
    },
    [refs, queueSubmitFocus],
  );

  const handleInput = (i: number, val: string) => {
    const cleaned = val.replace(/\D/g, "");
    if (cleaned.length > 1) {
      applyDigits(parseOtpDigits(cleaned));
      return;
    }
    const v = cleaned.slice(-1);
    const next_ = [...digits];
    next_[i] = v;
    setDigits(next_);
    if (isOtpComplete(next_)) {
      queueSubmitFocus();
    } else if (v && i < 5) {
      refs[i + 1].current?.focus();
    }
  };

  const handleKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text");
    const parsed = parseOtpDigits(pasted);
    if (parsed.some((d) => d !== "")) {
      e.preventDefault();
      applyDigits(parsed);
    }
  };

  const code = digits.join("");

  return { digits, refs, handleInput, handleKey, handlePaste, code, reset };
}

export function OtpInputs({
  digits,
  refs,
  onInput,
  onKey,
  onPaste,
}: {
  digits: string[];
  refs: React.RefObject<HTMLInputElement | null>[];
  onInput: (i: number, val: string) => void;
  onKey: (i: number, e: React.KeyboardEvent) => void;
  onPaste?: (e: React.ClipboardEvent) => void;
}) {
  const { t, tx } = useI18n();
  return (
    <div className={authText.otpRow}>
      {digits.map((v, i) => (
        <input
          key={i}
          ref={refs[i] as React.Ref<HTMLInputElement>}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={v}
          onChange={(e) => onInput(i, e.target.value)}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={onPaste}
          className={authText.otpDigit}
          aria-label={t("account.verification.codeDigitAriaLabel", { digit: i + 1 })}
        />
      ))}
    </div>
  );
}
